import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import BookingConsentModal from '../../components/AdvisorNetwork/BookingConsentModal';
import { useUpgrade } from '../../context/UpgradeContext';
import ProBadge from '../../components/upgrade/ProBadge';
import {
  addFollowingId,
  formatRelativeTime,
  formatSpecialisation,
  getFollowingIds,
  getStoredAdvisorId,
} from '../../utils/advisorNetworkConstants';

const VerifiedBadge = ({ credentialType }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-pine text-bone-raised text-label font-medium">
    <LockClosedIcon className="w-3 h-3" aria-hidden="true" />
    Verified {credentialType}
  </span>
);

const AdvisorProfile = ({ user }) => {
  const { advisor_id: advisorId } = useParams();
  const [searchParams] = useSearchParams();
  const packetId = searchParams.get('packet');
  const navigate = useNavigate();
  const { permissions, openUpgrade } = useUpgrade();

  const [advisor, setAdvisor] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [escalationBrief, setEscalationBrief] = useState(null);

  const storedAdvisorId = getStoredAdvisorId(user);
  const isOwnProfile = storedAdvisorId === advisorId;

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`/api/advisor-network/advisors/${advisorId}`);
        setAdvisor(response.data?.advisor || null);
        setPosts(response.data?.posts || []);
      } catch (err) {
        console.error('Failed to load advisor profile:', err);
        setError('Advisor not found.');
      } finally {
        setLoading(false);
      }
    };

    if (advisorId) loadProfile();
  }, [advisorId]);

  useEffect(() => {
    if (!packetId) return;
    const loadPacket = async () => {
      try {
        const res = await axios.get(`/api/advice/escalation/${packetId}`);
        setEscalationBrief(res.data?.packet?.advisor_brief || null);
      } catch (err) {
        console.warn('Could not load escalation brief:', err);
      }
    };
    loadPacket();
  }, [packetId]);

  useEffect(() => {
    if (user?.user_id && advisorId) {
      setFollowing(getFollowingIds(user.user_id).includes(advisorId));
    }
  }, [user?.user_id, advisorId]);

  const handleFollow = async () => {
    if (!user) {
      navigate('/auth', { state: { from: `/advisor-network/${advisorId}` } });
      return;
    }
    setFollowLoading(true);
    try {
      await axios.post(`/api/advisor-network/follow/${advisorId}`);
      setFollowing(true);
      addFollowingId(user.user_id, advisorId);
    } catch (err) {
      console.error('Follow failed:', err);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleBookClick = () => {
    if (!user) {
      navigate('/auth', { state: { from: `/advisor-network/${advisorId}` } });
      return;
    }
    if (!permissions?.can_book_advisor) {
      openUpgrade('book_advisor');
      return;
    }
    setShowConsentModal(true);
  };

  const submitBooking = useCallback(
    async (consented) => {
      setBookingLoading(true);
      try {
        const response = await axios.post(`/api/advisor-network/book/${advisorId}`, {
          aura_snapshot_consented: consented,
          escalation_packet_id: packetId || undefined,
        });
        setShowConsentModal(false);
        const bookingUrl = response.data?.booking_url;
        if (bookingUrl) {
          window.open(bookingUrl, '_blank', 'noopener,noreferrer');
        }
      } catch (err) {
        console.error('Booking failed:', err);
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 403 && typeof detail === 'object' && detail?.error === 'book_advisor_gated') {
          openUpgrade('book_advisor');
        }
      } finally {
        setBookingLoading(false);
      }
    },
    [advisorId, packetId],
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-4 border-2 border-hairline border-t-pine rounded-full animate-spin" />
          <p className="text-body text-ink-muted">Loading profile…</p>
        </div>
      </div>
    );
  }

  if (error || !advisor) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <p className="font-display text-h2 text-ink mb-2">Advisor not found</p>
          <p className="text-body text-ink-muted mb-6">{error}</p>
          <Link to="/advisor-network" className="btn-secondary">
            ← All Advisors
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <div className="max-w-3xl mx-auto px-6 md:px-8 py-10 md:py-14">
        <Link
          to="/advisor-network"
          className="inline-flex items-center text-body text-ink-muted hover:text-ink mb-8 transition-colors duration-300"
        >
          ← All Advisors
        </Link>

        <header className="pb-8 mb-8 border-b border-hairline">
          <h1 className="font-display text-h1 text-ink mb-3">{advisor.full_name}</h1>
          <div className="mb-3">
            <VerifiedBadge credentialType={advisor.credential_type} />
          </div>

          {(advisor.specialisations || []).length > 0 && (
            <p className="text-label text-ink-muted mb-3">
              {(advisor.specialisations || []).map((spec) => formatSpecialisation(spec)).join(', ')}
            </p>
          )}

          <p className="text-label text-ink-muted mb-4">
            {(advisor.follower_count ?? 0).toLocaleString()} followers
          </p>

          {user && (
            <button
              type="button"
              onClick={handleFollow}
              disabled={followLoading || following}
              className={`mb-4 px-4 py-2 rounded-md text-label font-medium border transition-colors duration-300 ${
                following
                  ? 'bg-pine text-bone-raised border-pine'
                  : 'bg-transparent text-pine border-pine hover:bg-pine-tint'
              }`}
            >
              {following ? 'Following' : 'Follow'}
            </button>
          )}

          {escalationBrief && (
            <section className="surface-panel p-4 mb-6 border border-pine/30 rounded-lg">
              <p className="text-label text-pine mb-2">Pre-briefed context from Aura</p>
              <p className="text-body text-ink whitespace-pre-wrap leading-relaxed">{escalationBrief}</p>
            </section>
          )}

          {advisor.bio && (
            <p className="text-body text-ink mb-6 leading-relaxed">{advisor.bio}</p>
          )}

          <button
            type="button"
            onClick={handleBookClick}
            disabled={bookingLoading}
            className="btn-primary w-full sm:w-auto min-w-[12rem] inline-flex items-center justify-center gap-2"
          >
            Book a consultation
            {!permissions?.can_book_advisor && <ProBadge feature="book_advisor" />}
          </button>
        </header>

        <section>
          <h2 className="text-body font-medium text-ink mb-6">Posts</h2>

          {posts.length === 0 ? (
            <p className="text-body text-ink-muted py-8 text-center border border-hairline rounded-lg bg-bone-raised">
              No posts yet.
            </p>
          ) : (
            <div className="hairline-list">
              {posts.map((post) => (
                <article key={post.post_id} className="py-6 first:pt-0">
                  {post.disclaimer_added && (
                    <span className="inline-block mb-2 px-2 py-0.5 rounded-md bg-pine-tint text-pine text-label">
                      Includes regulatory disclaimer
                    </span>
                  )}
                  {isOwnProfile && (post.flagged_words || []).length > 0 && (
                    <span className="inline-block mb-2 ml-2 px-2 py-0.5 rounded-md bg-warning/15 text-warning text-label border border-warning/30">
                      Contains flagged language
                    </span>
                  )}
                  <p className="text-body text-ink whitespace-pre-wrap leading-relaxed">
                    {post.content}
                  </p>
                  <p className="text-label text-ink-muted mt-3">
                    {formatRelativeTime(post.created_at)}
                  </p>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      {showConsentModal && (
        <BookingConsentModal
          advisorName={advisor.full_name}
          onShareAndBook={() => submitBooking(true)}
          onBookWithoutSharing={() => submitBooking(false)}
          onClose={() => setShowConsentModal(false)}
        />
      )}
    </div>
  );
};

export default AdvisorProfile;
